import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import AccountLayout from "../../blocs/AccountLayout";
import Table from "../../components/Table";
import Skeleton from "react-loading-skeleton";
import Popup from "../../components/Popup";
import EditRdvStandart from "../../components/rdvoffcanvas/EditRdvStandart";

import { fetchRendezvousExceptEnAttente } from "../../store/slices/rendezVousPrestaSlice";
import { formatDecimalHour, StatusRdv } from "../../utils/utils";
import { RefusRdv } from "../../components/rdvoffcanvas/RefusRdv.js";
import RendezVousSkeleton from "../../components/skeletons/RendezVousSkeleton.jsx";
import TooltipTwyny from "../../components/tooltip.jsx";

const RendezVousPresta = () => {
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupActionType, setPopupActionType] = useState(null);
  const [selectedRdvId, setSelectedRdvId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { sansEnAttente, status } = useSelector((state) => state.rdvPresta);
  // console.log("sansEnAttente:",sansEnAttente)
  useEffect(() => {
    dispatch(fetchRendezvousExceptEnAttente());
  }, [dispatch]);

  const handlePopupAction = () => {
    // console.log("selectedRdvId:",selectedRdvId)
    RefusRdv({
      dispatch,
      rendezVous: selectedRdvId,
      handleClose: () => setPopupVisible(false),
      setPopupVisible,
    });
  };

  const columns = useMemo(() => {
    const statusIcons = StatusRdv();
    return [
      {
        Header: "Date de réservation",
        accessor: "reservationDate",
      },
      {
        Header: "Statut",
        accessor: "statut",
        Cell: ({ value }) => (
          <span className={`icon-status ${value}`}>
            <img src={statusIcons[value]} alt={value} />
          </span>
        ),
      },
      {
        Header: "Client",
        accessor: "client",
      },
      {
        Header: "Lieu",
        accessor: "location",
      },
      {
        Header: "Prestation",
        accessor: "service",
      },
      {
        Header: "Temps",
        accessor: "time",
      },
      {
        Header: "Total TTC",
        accessor: "totalTTC",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const { id, statut, start_time } = row.original;
          const handleEditClick = (e) => {
            e.stopPropagation();
            setSelectedEvent(row.original);
            // console.log("row.original",row.original)
            setShowEdit(true);
          };

          const handleDeleteClick = (e) => {
            e.stopPropagation();
            setSelectedRdvId(row.original);
            setPopupActionType("delete");
            setPopupVisible(true);
          };

          const handleCommentClick = (e) => {
            e.stopPropagation();
            navigate(`/compte/rendez-vous/${id}`);
          };

          return (
            <div
              className="action-icons"
              onClick={() => navigate(`/compte/rendez-vous/${id}`)}
            >
              {statut === "confirme" && (
                <>
                  <button
                    onClick={handleDeleteClick}
                    className="action-btn delete"
                  >
                    <TooltipTwyny
                      className="left100"
                      children={<img src="/images/delete.svg" alt="Delete" />}
                      text="Annuler le rendez-vous"
                    />
                  </button>
                  <button onClick={handleEditClick} className="action-btn edit">
                    <TooltipTwyny
                      className="left100"
                      children={<img src="/images/edit.svg" alt="edit icon" />}
                      text="Reprogrammer le rendez-vous"
                    />
                  </button>
                </>
              )}
              {statut === "passe" && (
                <button
                  onClick={handleCommentClick}
                  className="action-btn comment"
                >
                  <TooltipTwyny
                    children={<img src="/images/comment.svg" alt="Comment" />}
                    text="Voir l'avis client"
                    className="left100"
                  />
                </button>
              )}
            </div>
          );
        },
      },
    ];
  }, [navigate]);

  const pagination = {
    canPreviousPage: !!sansEnAttente?.data?.prev_page_url,
    canNextPage: !!sansEnAttente?.data?.next_page_url,
    previousPageUrl: sansEnAttente?.data?.prev_page_url || null,
    nextPageUrl: sansEnAttente?.data?.next_page_url || null,
  };

  const handlePageChange = (url) => {
    if (url) {
      dispatch(fetchRendezvousExceptEnAttente({ url }));
    }
  };

  const data = useMemo(() => {
    if (!Array.isArray(sansEnAttente)) return [];

    return sansEnAttente.map((appt) => ({
      id: appt?.id,
      reservationDate:
        new Date(appt?.start_time).toLocaleDateString() +
        "\n" +
        new Date(appt?.start_time).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        //  timeZone: "Europe/Paris",
        }),
      statut: appt?.status,
      client: (
         <span>{appt?.user?.prenom} {appt?.user?.name} </span> 
      ),
      location:
        appt?.lieu_intervention === "enligne"
          ? appt?.meeting_link || "En ligne"
          : appt?.lieu_intervention === "onsite"
          ? appt?.adresse_domicile
          : appt?.lieu_intervention === "chezpro"
          ? `${appt?.etablissement?.code_postal} ${appt?.etablissement?.ville} ${appt?.etablissement?.adresse_postale}`
          : "N/A",
      service: appt?.prestations_snapshot?.[0]?.name || "N/A",
      time: formatDecimalHour(appt?.duration_hours),
      meeting_link: appt?.meeting_link,
      adresse_domicile: appt?.adresse_domicile,
      totalTTC: `${appt?.prestations_snapshot?.reduce((sum, pre) => sum + (parseFloat(pre?.ttc) || 0), 0)}€`,
      start_time: appt?.start_time,
      end_time: appt?.end_time,
      title: appt?.title,
      lieu_intervention: appt?.lieu_intervention,
      equipe_id: appt?.equipe_id,
      equipe: appt?.equipe,
      prestations_snapshot: appt?.prestations_snapshot?.map((prestation) => prestation?.name),
      equipe_id: appt?.equipe_id,
    }));
  }, [sansEnAttente]);

  return (
    <>
      <AccountLayout bgColor={"bgColor"}>
        <div className="section-title">
          <h3>Mes rendez-vous</h3>
        </div>

        {status === "loading" ? (
          <RendezVousSkeleton />
        ) : (
          <>
            {data?.length == 0 ? (
              <></>
            ) : (
              <Table
                columns={columns}
                data={data}
                onRowClick={"true"}
                path={"/compte/rendez-vous"}
                previousPage={() =>
                  handlePageChange(pagination.previousPageUrl)
                }
                nextPage={() => handlePageChange(pagination.nextPageUrl)}
              />
            )}
          </>
        )}
      </AccountLayout>

      {isPopupVisible && (
        <Popup
          isOpen={isPopupVisible}
          onClose={() => setPopupVisible(false)}
          onAction={() => handlePopupAction()}
          title="Attention, vous allez annuler un rendez-vous"
          text={<p className="body">Êtes-vous sur de vouloir poursuivre?</p>}
          actionButtonText="Annuler le rendez-vous"
          closeButtonText="Conserver"
          closeButtonClassName="bold600"
          color= "blue"
          icon={
            <svg
              width="88"
              height="88"
              viewBox="0 0 88 88"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="88"
                height="88"
                rx="14.6667"
                fill="#DD1E03"
                fillOpacity="0.1"
              />
              <path
                d="M44.4001 44.4001V34.5001M44.4001 51.7381V51.8251M64.2001 44.4001C64.2001 55.3353 55.3353 64.2001 44.4001 64.2001C33.4649 64.2001 24.6001 55.3353 24.6001 44.4001C24.6001 33.4649 33.4649 24.6001 44.4001 24.6001C55.3353 24.6001 64.2001 33.4649 64.2001 44.4001Z"
                stroke="#DD1E03"
                strokeWidth="4.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
      )}

      {showEdit && selectedEvent && (
        <EditRdvStandart
          show={showEdit}
          handleClose={() => setShowEdit(false)}
          event={selectedEvent}
        />
      )}
    </>
  );
};

export default RendezVousPresta;
